/**
 * AppActivityPage.tsx
 *
 * What the practice's own patient app is producing: appointments booked
 * through it and money collected through it, over a period the user chooses.
 *
 * WHY THIS EXISTS (SCRUM-879, from the franchisee call). The numbers already
 * existed — `/app-activity/stats` has served them for a while — but the only
 * screen reading them lives in the Corporate Dashboard, which franchisees do
 * not have. They asked to see their own location's figures in Prospera.
 *
 * SCOPE IS THE SERVER'S JOB, NOT A FILTER HERE. resolveAccountIds() derives the
 * accounts from the signed-in user, and a practice user resolves to exactly
 * their own account. There is deliberately no account picker: adding one would
 * imply a choice the API would refuse anyway.
 *
 * Deliberately NOT a port of the Corporate Dashboard's 1,186-line version. That
 * screen answers a corporate question — comparing locations against each other.
 * This one answers the franchisee's: how is MY location doing, over a period I
 * pick. The facility breakdown is kept because a single practice can still run
 * several locations.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, DollarSign, Smartphone, Users } from 'lucide-react';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { PageHeader } from '@/components/layout/PageHeader';
import { getAppActivityStats, type AppActivityFacilityRow } from '@/services/appActivity.service';
import { DatePicker } from '@/components/ui/DatePicker';

/** Preset windows, plus the custom range the ticket asked for. */
const RANGES = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'ytd', label: 'Year to date', days: null },
] as const;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

export default function AppActivityPage() {
  const [rangeKey, setRangeKey] = useState<string>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const { dateFrom, dateTo } = useMemo(() => {
    // A custom range wins as soon as BOTH ends are set — applying it half-filled
    // would silently query a window the user is still typing.
    if (customFrom && customTo) return { dateFrom: customFrom, dateTo: customTo };
    const preset = RANGES.find((r) => r.key === rangeKey) ?? RANGES[0];
    return {
      dateFrom: preset.days === null ? startOfYear() : isoDaysAgo(preset.days),
      dateTo: new Date().toISOString().slice(0, 10),
    };
  }, [rangeKey, customFrom, customTo]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['app-activity', dateFrom, dateTo],
    queryFn: () => getAppActivityStats({ dateFrom, dateTo }),
    staleTime: 60_000,
  });

  const appt = data?.appointments.totals;
  const pay = data?.payments.totals;

  // Revenue collected THROUGH THE APP: the three things it can sell. Summed
  // here rather than server-side because each is also shown on its own below —
  // one number that cannot disagree with its parts.
  const appRevenue = (pay?.productAmount ?? 0) + (pay?.packageAmount ?? 0) + (pay?.membershipAmount ?? 0);

  const facilities: AppActivityFacilityRow[] = data?.appointments.byFacility ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        variant="compact"
        title="App Activity"
        subtitle="Appointments booked and money collected through your patient app"
      />

      {/* ── Period ── */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-wrap gap-2">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => { setRangeKey(r.key); setCustomFrom(''); setCustomTo(''); }}
                className={
                  !customFrom && !customTo && rangeKey === r.key
                    ? 'rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700'
                    : 'rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'
                }
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div className="text-sm text-slate-600">
              <label htmlFor="app-activity-from" className="mb-1 block text-xs font-medium text-slate-500">From</label>
              <DatePicker
                id="app-activity-from"
                value={customFrom}
                max={customTo || undefined}
                onChange={setCustomFrom}
                wrapperClassName="w-40"
              />
            </div>
            <div className="text-sm text-slate-600">
              <label htmlFor="app-activity-to" className="mb-1 block text-xs font-medium text-slate-500">To</label>
              <DatePicker
                id="app-activity-to"
                value={customTo}
                min={customFrom || undefined}
                onChange={setCustomTo}
                wrapperClassName="w-40"
              />
            </div>
            {(customFrom || customTo) && (
              <button
                type="button"
                onClick={() => { setCustomFrom(''); setCustomTo(''); }}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="ml-auto text-xs text-slate-500">
            Showing {dateFrom} to {dateTo}
          </p>
        </div>
      </Card>

      {isError ? (
        <Card className="p-6">
          <p className="text-sm text-rose-600">
            {(error as Error)?.message || 'Could not load app activity.'}
          </p>
          {/* The endpoint refuses an account with no subdomain, which reads as a
              permissions error rather than a setup gap — say which it is. */}
          <p className="mt-1 text-xs text-slate-500">
            If this says no companies are assigned, the practice needs a subdomain set before app
            activity can be reported.
          </p>
        </Card>
      ) : isFetching && !data ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          <MetricCardGrid columns={4}>
            <StatCard
              label="Booked in the app"
              value={appt?.appAppointments ?? 0}
              icon={<Smartphone className="h-5 w-5" />}
              tone="violet"
            />
            <StatCard
              label="All appointments"
              value={appt?.totalAppointments ?? 0}
              icon={<CalendarCheck className="h-5 w-5" />}
              tone="blue"
            />
            <StatCard label="Collected in the app" value={money(appRevenue)} icon={<DollarSign className="h-5 w-5" />} tone="emerald" />
            <StatCard label="Patients using the app" value={appt?.uniquePatients ?? 0} icon={<Users className="h-5 w-5" />} tone="sky" />
          </MetricCardGrid>

          <Card className="p-5">
            <h2 className="mb-1 text-lg font-semibold text-slate-900">What the app sold</h2>
            <p className="mb-4 text-sm text-slate-500">
              The three things a patient can buy in the app, over the selected period.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { label: 'Products', count: pay?.productCount ?? 0, amount: pay?.productAmount ?? 0 },
                { label: 'Packages', count: pay?.packageCount ?? 0, amount: pay?.packageAmount ?? 0 },
                { label: 'Memberships', count: pay?.membershipCount ?? 0, amount: pay?.membershipAmount ?? 0 },
              ].map((row) => (
                <div key={row.label} className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                  <p className="text-sm font-medium text-slate-600">{row.label}</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{money(row.amount)}</p>
                  <p className="text-xs text-slate-500">{row.count.toLocaleString()} purchases</p>
                </div>
              ))}
            </div>
          </Card>

          {facilities.length > 0 && (
            <Card className="p-5">
              <h2 className="mb-1 text-lg font-semibold text-slate-900">By location</h2>
              <p className="mb-4 text-sm text-slate-500">
                Appointments booked in the app, per location.
              </p>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2 text-right">Booked in app</th>
                      <th className="px-3 py-2 text-right">All appointments</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {facilities.map((f) => (
                      <tr key={f.facilityID ?? 'none'} className="hover:bg-slate-50">
                        {/* A facility with no name is real data, not a bug —
                            appointments with no location must stay countable. */}
                        <td className="px-3 py-2 text-slate-900">{f.name || 'No location'}</td>
                        <td className="px-3 py-2 text-right font-medium text-slate-900">
                          {(f.appAppointments ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">
                          {(f.totalAppointments ?? 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
